import { Autocomplete, Box, HorizontalStack, Tag, Text, VerticalStack } from '@shopify/polaris';
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getProductTags } from '../../../services/productService';

/**
 * Editor de tags del producto.
 *
 * Los tags viven en `Products.tags` (columna JSON) y son lo que Hopper publica
 * en Shopify. Se muestran TODOS, incluidos los derivados del AS400
 * (`color_*`, `talla_*`, `tipo_*`, `__tab*`), porque de ellos salen la opción de
 * color, el género y el tipo de producto en Shopify — ocultarlos haría que el
 * usuario no entendiese por qué su producto sale sin color.
 *
 * Autocompleta con los tags ya usados en el catálogo para que no proliferen
 * variantes ortográficas del mismo tag ("rebajas" / "Rebajas" / "rebaja").
 */
const ProductTagsField = ({ value = [], onChange, disabled = false }) => {
  const [inputValue, setInputValue] = useState('');

  const { data: suggestions = [] } = useQuery(['productTags'], () => getProductTags().then((r) => r.data?.data ?? []), {
    staleTime: 1000 * 60 * 5,
  });

  const options = useMemo(() => {
    const search = inputValue.trim().toLowerCase();
    const alreadyUsed = new Set(value);

    return suggestions
      .filter((tag) => !alreadyUsed.has(tag))
      .filter((tag) => (search ? tag.toLowerCase().includes(search) : true))
      .slice(0, 50)
      .map((tag) => ({ label: tag, value: tag }));
  }, [suggestions, inputValue, value]);

  const addTag = useCallback(
    (tag) => {
      const clean = String(tag).trim();
      if (!clean || value.includes(clean)) return;
      onChange([...value, clean]);
      setInputValue('');
    },
    [value, onChange],
  );

  const removeTag = useCallback((tag) => () => onChange(value.filter((t) => t !== tag)), [value, onChange]);

  // Enter añade lo escrito aunque no esté en las sugerencias: los tags son libres.
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    addTag(inputValue);
  };

  return (
    <VerticalStack gap="2">
      <div onKeyDown={handleKeyDown}>
        <Autocomplete
          options={options}
          selected={[]}
          onSelect={(selected) => addTag(selected[0])}
          textField={
            <Autocomplete.TextField
              label="Tags"
              value={inputValue}
              onChange={setInputValue}
              autoComplete="off"
              disabled={disabled}
              placeholder="Escribe un tag y pulsa Enter"
              helpText="Los tags se publican en Shopify. Los que empiezan por color_, talla_ o tipo_ alimentan la opción de color, la talla y el tipo de producto."
            />
          }
        />
      </div>

      {value.length > 0 ? (
        <Box paddingBlockStart="1">
          <HorizontalStack gap="1" wrap>
            {value.map((tag) => (
              <Tag key={tag} onRemove={disabled ? undefined : removeTag(tag)}>
                {tag}
              </Tag>
            ))}
          </HorizontalStack>
        </Box>
      ) : (
        <Text variant="bodySm" color="subdued">
          Sin tags. El producto se publicará en Shopify sin etiquetas.
        </Text>
      )}
    </VerticalStack>
  );
};

export default ProductTagsField;
